import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC] px-4" data-testid="reset-password-page">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_campaign-leads-1/artifacts/9fqxo1et_PomenLogo_blueBG.png"
            alt="POMEN Logo"
            className="h-14 mx-auto rounded-xl mb-4"
          />
          <h1 className="text-2xl font-semibold text-gray-900">Set New Password</h1>
        </div>

        <Card className="border border-gray-200 rounded-xl bg-white shadow-sm">
          <CardContent className="p-6">
            {done ? (
              <div className="text-center space-y-4" data-testid="reset-success">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm text-gray-700">Your password has been reset successfully.</p>
                <Button onClick={() => navigate("/login")} className="w-full bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">{error}</div>}
                {!token && <div className="p-3 bg-yellow-50 text-yellow-700 text-sm rounded-lg border border-yellow-200">No reset token found. Please use the link from your email.</div>}
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="border-gray-300 rounded-lg pr-10" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm Password</Label>
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="border-gray-300 rounded-lg" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" disabled={submitting || !token} className="w-full bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg h-11" data-testid="reset-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Reset Password
                </Button>
                <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 block text-center">Back to Sign In</Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
